/**
 * What `user_connections.brand_kit_id` means, encoded once.
 *
 * This is the file the production migration's column COMMENT
 * (20260820230216_add_brand_kit_id_to_user_connections.sql) and
 * `app-sidebar.tsx` both point at as "the single place that encodes this."
 * An earlier revision of it lived only on
 * `origin/worktree-integrations-account-switching-research` (59406170c), so
 * for two days `main` documented a seam `main` did not have. This version
 * supersedes that one and adds the precedence rule it was missing.
 *
 * NULL = ACCOUNT-WIDE, not "unassigned, visible nowhere." That is the only
 * reading under which all 45 production rows (100% NULL as of 2026-08-22) and
 * every `user_id`-only call site stay correct with zero backfill. It
 * deliberately DIVERGES from the `competitors` / `social_posts` precedent,
 * where NULL means "legacy row awaiting assignment." Here NULL is a
 * permanent, valid steady state.
 */

export interface BrandScopedConnection {
  brand_kit_id: string | null;
}

export interface PlatformScopedConnection extends BrandScopedConnection {
  platform: string;
}

/**
 * Is this connection *visible* to a workspace at all?
 *
 * `brandKitId: null` means "no brand filter requested" — the caller wants
 * every connection regardless of workspace, which is what a `user_id`-only
 * call site does today. Passing null is a pure no-op filter. It must never be
 * confused with "the connection's own brand_kit_id is null."
 *
 * Visibility is NOT the same question as which connection to publish through;
 * for that, use the precedence-aware resolvers below.
 */
export function isConnectionVisibleToBrandKit(
  connection: BrandScopedConnection,
  brandKitId: string | null,
): boolean {
  if (brandKitId === null) return true;
  return connection.brand_kit_id === null || connection.brand_kit_id === brandKitId;
}

/** Visibility filter over an already-fetched array. No precedence applied. */
export function filterConnectionsForBrandKit<T extends BrandScopedConnection>(
  connections: T[],
  brandKitId: string | null,
): T[] {
  return connections.filter((connection) => isConnectionVisibleToBrandKit(connection, brandKitId));
}

/**
 * SPECIFIC BEATS ACCOUNT-WIDE, for the rows of ONE platform.
 *
 * Visibility alone is not enough at publish time. A workspace can see both its
 * own Instagram row and a legacy account-wide Instagram row; handing both to
 * `getActivePlatformConnection` yields `status: 'ambiguous'` and blocks
 * publishing — a regression introduced by scoping, on an account that
 * published fine before. Owner `cc0a0d82` (2 kits, 9 active connections) is
 * the one production account that would hit it.
 *
 * So: if this workspace owns any row for the platform, those rows ARE the
 * answer and account-wide rows are ignored. Only when it owns none do the
 * account-wide rows apply. With today's all-NULL dataset the first branch
 * never fires, so behaviour is byte-identical to no filtering — which is the
 * point: this must stay inert until something writes a non-NULL brand_kit_id.
 *
 * Callers must pass rows for a SINGLE platform. Use
 * `resolveWorkspaceConnections` for a mixed set.
 */
export function resolveWorkspaceConnectionsForPlatform<T extends BrandScopedConnection>(
  connections: T[],
  brandKitId: string | null,
): T[] {
  if (brandKitId === null) return connections;

  const ownedByThisWorkspace = connections.filter((c) => c.brand_kit_id === brandKitId);
  if (ownedByThisWorkspace.length > 0) return ownedByThisWorkspace;

  return connections.filter((c) => c.brand_kit_id === null);
}

/**
 * The same precedence, applied PER PLATFORM across a mixed set.
 *
 * Grouping matters: applied to the whole set at once, a single scoped
 * Instagram row would suppress an account-wide TikTok row and silently
 * disconnect a platform the workspace legitimately publishes to.
 *
 * Input order is preserved.
 */
export function resolveWorkspaceConnections<T extends PlatformScopedConnection>(
  connections: T[],
  brandKitId: string | null,
): T[] {
  if (brandKitId === null) return connections;

  const platformsOwned = new Set(
    connections.filter((c) => c.brand_kit_id === brandKitId).map((c) => c.platform),
  );

  return connections.filter((c) =>
    platformsOwned.has(c.platform) ? c.brand_kit_id === brandKitId : c.brand_kit_id === null,
  );
}
